import React, { useCallback, useEffect, useRef, useState } from 'react';
import useVideoContentDimensions from './useVideoContentDimensions';
import { BASE_VU_WIDTH } from '../../common/constants/common';
import { VIDEO_ORIENTATIONS } from '../utils/SimulatedItemsUtility';

const OBJECT_TYPE = {
  logo: 'logo',
  textPanel: 'textPanel',
  callAction: 'callAction',
};

export default function useObjectSimulation({
  parentNode,
  id,
  margin_x,
  margin_y,
  position,
  size,
  mode,
  imageDimensions,
  updateSimulation,
  maxDimensions = null,
  mixedOrientation = false,
  multiplyMode = false,
  isEmptyVideo = false,
  isLandscapePreview = false,
  isPortraitPreview = false,
  isSquarePreview = false,
}) {
  const [objectPosition, setObjectPosition] = useState({});
  const [objectSize, setObjectSize] = useState({});

  const {
    loading,
    scaleRatio,
    setLoading,
    offsetHeight,
    orientation,
    offsetWidth,
    smallestSide,
    videoContentWidth,
    videoContentHeight,
  } = useVideoContentDimensions({
    id,
    parentNode,
    maxDimensions,
    mixedOrientation,
    multiplyMode,
    isEmptyVideo,
    isLandscapePreview,
    isPortraitPreview,
    isSquarePreview,
  });

  const getOrientationRatio = () => {
    switch (orientation) {
      case VIDEO_ORIENTATIONS.landscape:
        return { x: 1.1, y: 0.75 };
      case VIDEO_ORIENTATIONS.square:
        return { x: 0.75, y: 0.75 };
      default:
        return { x: 1, y: 1 };
    }
  };

  const recalculateTimeoutRef = useRef(null);

  const recalculateVuToPx = useCallback(
    valueInVu => {
      return (valueInVu * smallestSide) / BASE_VU_WIDTH;
    },
    [smallestSide]
  );

  const calculateStyles = () => {
    const styles = {};
    if (videoContentWidth > 0 && videoContentHeight > 0) {
      switch (mode) {
        case OBJECT_TYPE.logo: {
          if (imageDimensions?.width > 0) {
            // Recalculate logo positions offsets
            const pxMarginX = (smallestSide * margin_x) / 100;
            const pxMarginY = (smallestSide * margin_y) / 100;
            // Ignore this value for the preview logo, since there is no actual 16 by 9 player for portrait and square
            const videoTopStartPosition = isPortraitPreview || isSquarePreview ? 0 : (offsetHeight - videoContentHeight) / 2;
            const videoLeftStartPosition = isPortraitPreview || isSquarePreview ? 0 : (offsetWidth - videoContentWidth) / 2;
            // Logo start position from left or right
            switch (position) {
              case 'tr':
                styles.top = videoTopStartPosition + pxMarginY;
                styles.right = videoLeftStartPosition + pxMarginX;
                break;
              case 'tl':
                styles.top = videoTopStartPosition + pxMarginY;
                styles.left = videoLeftStartPosition + pxMarginX;
                break;
              case 'tc':
                styles.top = videoTopStartPosition + pxMarginY;
                styles.left = videoLeftStartPosition + videoContentWidth / 2;
                styles.transform = 'translateX(-50%)';
                break;
              case 'br':
                styles.bottom = videoTopStartPosition + pxMarginY;
                styles.right = videoLeftStartPosition + pxMarginX;
                break;
              case 'bl':
                styles.bottom = videoTopStartPosition + pxMarginY;
                styles.left = videoLeftStartPosition + pxMarginX;
                break;
              case 'bc':
                styles.bottom = videoTopStartPosition + pxMarginY;
                styles.left = videoLeftStartPosition + videoContentWidth / 2;
                styles.transform = 'translateX(-50%)';
                break;
            }
            const logoRatio = imageDimensions.width / imageDimensions.height;
            // Calculate max logo values according to base width (320px) and configured size

            let logoWidthCoefficient = 0;
            switch (orientation) {
              case VIDEO_ORIENTATIONS.square:
                logoWidthCoefficient = 0.75;
                break;
              case VIDEO_ORIENTATIONS.landscape:
                logoWidthCoefficient = 0.5625;
                break;
              default:
                logoWidthCoefficient = 1;
            }
            // Recalculate logo img size taking into account video size and scaleRatio
            const simulationWidth = (logoWidthCoefficient * size * videoContentWidth) / 100;
            const simulationHeight = simulationWidth / logoRatio;
            setObjectSize({ width: Math.round(simulationWidth), height: Math.round(simulationHeight) });
          }
          break;
        }
        case OBJECT_TYPE.textPanel: {
          const orientationRatio = getOrientationRatio();

          const horzFactor = position == 'tc' || position == 'bc' ? 0 : (margin_x / 100) * orientationRatio.x;
          const vertFactor = (margin_y / 100) * orientationRatio.y;
          // Ignore this value for the preview text panel, since there is no actual 16 by 9 player for portrait and square
          const textPanelLeft =
            smallestSide * horzFactor + (isPortraitPreview || isSquarePreview ? 0 : (offsetWidth - videoContentWidth) / 2);
          const textPanelTop =
            smallestSide * vertFactor + (isPortraitPreview || isSquarePreview ? 0 : (offsetHeight - videoContentHeight) / 2);

          switch (position) {
            case 'tr':
              styles.top = textPanelTop;
              styles.right = textPanelLeft;
              break;
            case 'tl':
              styles.top = textPanelTop;
              styles.left = textPanelLeft;
              break;
            case 'tc':
              styles.top = textPanelTop;
              styles.left = textPanelLeft + videoContentWidth / 2;
              styles.transform = 'translateX(-50%)';
              break;
            case 'br':
              styles.bottom = textPanelTop;
              styles.right = textPanelLeft;
              break;
            case 'bl':
              styles.bottom = textPanelTop;
              styles.left = textPanelLeft;
              break;
            case 'bc':
              styles.bottom = textPanelTop;
              styles.left = textPanelLeft + videoContentWidth / 2;
              styles.transform = 'translateX(-50%)';
              break;
          }
          break;
        }
        case OBJECT_TYPE.callAction: {
          const videoTopStartPosition = (offsetHeight - videoContentHeight) / 2;
          const videoLeftStartPosition = (offsetWidth - videoContentWidth) / 2;

          const marginFromEdge = 0.05; // 5%

          switch (position) {
            case 'top_right':
              styles.top = videoTopStartPosition + videoContentHeight * marginFromEdge;
              styles.right = videoLeftStartPosition + videoContentWidth * marginFromEdge;
              styles.maxWidth = videoContentWidth - videoContentWidth * marginFromEdge * 2;
              break;
            case 'top_middle':
              styles.top = videoTopStartPosition + videoContentHeight * marginFromEdge;
              styles.left = videoLeftStartPosition + videoContentWidth / 2;
              styles.maxWidth = videoContentWidth - videoContentWidth * marginFromEdge * 2;
              break;
            case 'top_left':
              styles.top = videoTopStartPosition + videoContentHeight * marginFromEdge;
              styles.left = videoLeftStartPosition + videoContentWidth * marginFromEdge;
              styles.maxWidth = videoContentWidth - videoContentWidth * marginFromEdge * 2;
              break;
            case 'bottom_right':
              styles.bottom = videoTopStartPosition + videoContentHeight * marginFromEdge;
              styles.right = videoLeftStartPosition + videoContentWidth * marginFromEdge;
              styles.maxWidth = videoContentWidth - videoContentWidth * marginFromEdge * 2;
              break;
            case 'bottom_middle':
              styles.bottom = videoTopStartPosition + videoContentHeight * marginFromEdge;
              styles.left = videoLeftStartPosition + videoContentWidth / 2;
              styles.maxWidth = videoContentWidth - videoContentWidth * marginFromEdge * 2;
              break;
            case 'bottom_left':
              styles.bottom = videoTopStartPosition + videoContentHeight * marginFromEdge;
              styles.left = videoLeftStartPosition + videoContentWidth * marginFromEdge;
              styles.maxWidth = videoContentWidth - videoContentWidth * marginFromEdge * 2;
              break;
            case 'middle':
              styles.bottom = videoTopStartPosition + videoContentHeight / 2;
              styles.left = videoLeftStartPosition + videoContentWidth / 2;
              styles.maxWidth = videoContentWidth - videoContentWidth * marginFromEdge * 2;
              break;
            case 'middle_left':
              styles.bottom = videoTopStartPosition + videoContentHeight / 2;
              styles.left = videoLeftStartPosition + videoContentWidth * marginFromEdge;
              styles.maxWidth = videoContentWidth - videoContentWidth * marginFromEdge * 2;
              break;
            case 'middle_right':
              styles.bottom = videoTopStartPosition + videoContentHeight / 2;
              styles.right = videoLeftStartPosition + videoContentWidth * marginFromEdge;
              styles.maxWidth = videoContentWidth - videoContentWidth * marginFromEdge * 2;
              break;
          }
          break;
        }
      }
      for (const key in styles) {
        if (!isNaN(styles[key])) {
          styles[key] = `${Math.round(styles[key])}px`;
        }
      }
      setObjectPosition(styles);
    }
  };

  useEffect(() => {
    setLoading(true);

    if (recalculateTimeoutRef?.current) {
      clearTimeout(recalculateTimeoutRef.current);
      recalculateTimeoutRef.current = null;
    } else {
      setObjectSize({});
    }
    recalculateTimeoutRef.current = setTimeout(() => {
      calculateStyles();
      recalculateTimeoutRef.current = null;
      setLoading(false);
    }, 50);
  }, [videoContentHeight, videoContentWidth, offsetHeight, offsetWidth, imageDimensions, margin_x, margin_y, position, size, updateSimulation, smallestSide]);

  return {
    objectPosition,
    objectSize,
    recalculateVuToPx,
    loading,
    videoContentWidth,
    videoContentHeight,
  };
}
